import React, { useState, useEffect, useRef } from 'react';
import {
  getLocalAddressBook,
  syncAddressBookFromServer,
  saveAddressBookContacts,
  deleteAddressBookContact,
  clearAddressBook,
  parseContactsCsv,
  downloadExampleCsv,
} from '../services/addressBookService';
import { useTranslation } from '../hooks/useTranslation';
import { makeTr, trDyn } from '../services/trLookup';

/**
 * 📒 Adressbuch-Dialog (für Büro/Empfang, Wohnanlage/Wohnheim, Hotel & Paketshop)
 *
 * - CSV-Import (akzeptiert unser Beispiel-CSV sowie Microsoft Outlook/365/Entra & Google Contacts/Workspace)
 * - Beispiel-CSV herunterladen
 * - Einzelne Empfänger manuell anlegen / suchen / löschen
 */
export default function AddressBookModal({ colors, lang = 'de', onClose }) {
  const dark = !!colors?.darkMode;
  const c = {
    bg: dark ? '#23272f' : '#ffffff',
    card: dark ? '#1b1f27' : '#f6f8fa',
    text: dark ? '#ffffff' : '#1f2328',
    sub: dark ? '#9ca3af' : '#57606a',
    border: dark ? '#374151' : '#d0d7de',
    primary: '#1976d2',
  };

  const tr = makeTr(lang);
  const { t } = useTranslation();

  const [contacts, setContacts] = useState(() => getLocalAddressBook());
  const [search, setSearch] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [department, setDepartment] = useState('');
  const [shelf, setShelf] = useState('');
  const [note, setNote] = useState('');
  const [statusMsg, setStatusMsg] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    let active = true;
    syncAddressBookFromServer().then((list) => {
      if (active && Array.isArray(list)) setContacts(list);
    });
    return () => { active = false; };
  }, []);

  const resetFormFields = () => {
    setEditingId(null);
    setName('');
    setEmail('');
    setDepartment('');
    setShelf('');
    setNote('');
  };

  const handleStartEdit = (item) => {
    setEditingId(item.id);
    setName(item.name || '');
    setEmail(item.email || '');
    setDepartment(item.department || '');
    setShelf(item.shelf || '');
    setNote(item.note || '');
    setStatusMsg(null);
  };

  const handleAddManual = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setBusy(true);
    try {
      // Falls beim Bearbeiten Name/E-Mail geändert wurden und sich die ID ändert, alten Eintrag löschen
      if (editingId) {
        const existing = contacts.find((x) => x.id === editingId);
        if (existing && (existing.name !== name.trim() || existing.email !== email.trim().toLowerCase())) {
          await deleteAddressBookContact(editingId);
        }
      }
      const updated = await saveAddressBookContacts([{
        id: editingId || undefined,
        name: name.trim(),
        email: email.trim(),
        department: department.trim(),
        shelf: shelf.trim(),
        note: note.trim(),
      }]);
      setContacts(updated);
      const wasEdit = !!editingId;
      resetFormFields();
      setStatusMsg({
        type: 'ok',
        text: wasEdit
          ? tr('Änderungen gespeichert ✓', 'Changes saved ✓', 'Wijzigingen opgeslagen ✓')
          : tr('Kontakt gespeichert ✓', 'Contact saved ✓', 'Contact opgeslagen ✓'),
      });
    } finally {
      setBusy(false);
    }
  };

  const handleCsvFile = async (e) => {
    const file = e.target.files?.[0];
    if (fileRef.current) fileRef.current.value = '';
    if (!file) return;
    setBusy(true);
    setStatusMsg(null);
    try {
      const text = await file.text();
      const { contacts: parsed, formatDetected, skipped } = parseContactsCsv(text);
      if (!parsed.length) {
        setStatusMsg({
          type: 'err',
          text: tr(
            'Keine gültigen Kontakte in der CSV-Datei gefunden. Bitte prüfe die Spalten (Name, E-Mail).',
            'No valid contacts found in CSV file. Please check columns (Name, Email).',
            'Geen geldige contacten gevonden in CSV-bestand.'
          ),
        });
        setBusy(false);
        return;
      }
      const updated = await saveAddressBookContacts(parsed);
      setContacts(updated);
      const formatLabel = {
        microsoft: 'Microsoft 365 / Outlook',
        google: 'Google Contacts / Workspace',
        standard: t('ui.addressBook.csvStandard'),
        headerless: t('ui.addressBook.csvHeaderless'),
      }[formatDetected] || 'CSV';
      setStatusMsg({
        type: 'ok',
        text: trDyn(lang, 'd28', { count: parsed.length, format: formatLabel }),
      });
    } catch (err) {
      setStatusMsg({
        type: 'err',
        text: tr('Fehler beim Lesen der CSV-Datei.', 'Error reading CSV file.', 'Fout bij lezen van CSV-bestand.'),
      });
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (id) => {
    if (editingId === id) resetFormFields();
    const next = await deleteAddressBookContact(id);
    setContacts(next);
  };

  const handleClearAll = async () => {
    if (!window.confirm(trDyn(lang, 'd29', { count: contacts.length }))) return;
    setBusy(true);
    resetFormFields();
    const next = await clearAddressBook();
    setContacts(next);
    setBusy(false);
  };

  const q = search.trim().toLowerCase();
  const filtered = q
    ? contacts.filter((x) =>
        (x.name || '').toLowerCase().includes(q) ||
        (x.email || '').toLowerCase().includes(q) ||
        (x.department || '').toLowerCase().includes(q) ||
        (x.shelf || '').toLowerCase().includes(q) ||
        (x.note || '').toLowerCase().includes(q)
      )
    : contacts;

  return (
    <div
      data-testid="addressbook-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', zIndex: 3200,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 12,
      }}
    >
      <div style={{
        background: c.bg, color: c.text, borderRadius: 14, width: '100%', maxWidth: 560,
        maxHeight: '92vh', display: 'flex', flexDirection: 'column',
        boxShadow: '0 12px 40px rgba(0,0,0,0.4)',
      }}>
        {/* Header */}
        <div style={{
          padding: '14px 18px', borderBottom: `1px solid ${c.border}`,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <div>
            <strong style={{ fontSize: 17 }}>
              📒 {tr('Empfänger- & Stammkunden-Liste (CSV)', 'Recipient & Regular Customer List (CSV)', 'Ontvangers- & Vaste-klantenlijst (CSV)')}
            </strong>
            <div style={{ fontSize: 12, color: c.sub, marginTop: 2 }}>
              {trDyn(lang, 'd30', { count: contacts.length })}
            </div>
          </div>
          <button
            type="button"
            data-testid="addressbook-close"
            onClick={onClose}
            style={{ background: 'transparent', border: 'none', fontSize: 26, cursor: 'pointer', color: c.text, lineHeight: 1 }}
          >
            ×
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: '14px 18px', overflowY: 'auto', flex: 1 }}>
          {/* CSV Import / Download Box */}
          <div style={{
            padding: 12, borderRadius: 10, background: c.card, border: `1px solid ${c.border}`, marginBottom: 14,
          }}>
            <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 6 }}>
              📥 {tr('CSV-Liste importieren (Stammkunden / Mitarbeiter / Bewohner)', 'Import CSV List (Regular Customers / Staff / Residents)', 'CSV-lijst importeren')}
            </div>
            <div style={{ fontSize: 12, color: c.sub, lineHeight: 1.45, marginBottom: 10 }}>
              {tr(
                'Lade unsere Beispiel-CSV herunter (enthält Spalten für Name, E-Mail, Adresse/Abteilung, Stammfach & Hinweis für Mitarbeiter) oder importiere direkt aus Excel, Microsoft Outlook/365 oder Google Kontakte.',
                'Download our example CSV (includes columns for Name, Email, Address/Dept, Default Shelf & Staff Note) or import directly from Excel, Microsoft Outlook/365, or Google Contacts.',
                'Download ons voorbeeld-CSV (met naam, e-mail, afdeling/adres, stamvak & notitie voor medewerkers) of upload direct vanuit Excel, Microsoft of Google.'
              )}
            </div>
            <input
              ref={fileRef}
              type="file"
              accept=".csv,text/csv,text/plain"
              onChange={handleCsvFile}
              style={{ display: 'none' }}
            />
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button
                type="button"
                data-testid="addressbook-import-btn"
                disabled={busy}
                onClick={() => fileRef.current?.click()}
                style={{
                  flex: 1, minWidth: 170, padding: '9px 12px', borderRadius: 8, border: 'none',
                  background: c.primary, color: '#fff', fontWeight: 600, fontSize: 13, cursor: 'pointer',
                }}
              >
                📂 {tr('CSV-Datei auswählen…', 'Choose CSV file…', 'CSV-bestand kiezen…')}
              </button>
              <button
                type="button"
                data-testid="addressbook-example-btn"
                onClick={downloadExampleCsv}
                style={{
                  padding: '9px 12px', borderRadius: 8, border: `1px solid ${c.border}`,
                  background: 'transparent', color: c.text, fontWeight: 600, fontSize: 13, cursor: 'pointer',
                }}
              >
                📄 {tr('Beispiel-CSV herunterladen', 'Download Example CSV', 'Voorbeeld-CSV downloaden')}
              </button>
            </div>
            {statusMsg && (
              <div
                data-testid="addressbook-status"
                style={{
                  marginTop: 10, padding: '8px 10px', borderRadius: 8, fontSize: 12, fontWeight: 600,
                  background: statusMsg.type === 'ok' ? (dark ? '#132f20' : '#e6f4ea') : (dark ? '#3b181b' : '#fce8e6'),
                  color: statusMsg.type === 'ok' ? (dark ? '#6ee7b7' : '#137333') : (dark ? '#fca5a5' : '#c5221f'),
                }}
              >
                {statusMsg.text}
              </div>
            )}
          </div>

          {/* Manuell hinzufügen / Bearbeiten */}
          <form onSubmit={handleAddManual} style={{
            padding: 12, borderRadius: 10, border: `1.5px solid ${editingId ? c.primary : c.border}`, marginBottom: 14,
            background: editingId ? (dark ? '#172234' : '#f0f7ff') : 'transparent',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <div style={{ fontSize: 13, fontWeight: 700 }}>
                {editingId
                  ? `✏️ ${tr('Eintrag bearbeiten', 'Edit Entry', 'Vermelding bewerken')}`
                  : `➕ ${tr('Empfänger / Stammkunde hinzufügen', 'Add Recipient / Regular Customer', 'Ontvanger / vaste klant toevoegen')}`}
              </div>
              {editingId && (
                <button
                  type="button"
                  onClick={resetFormFields}
                  style={{
                    background: 'transparent', border: 'none', color: c.sub, fontSize: 12,
                    fontWeight: 600, cursor: 'pointer', padding: '2px 6px',
                  }}
                >
                  {tr('Abbrechen', 'Cancel', 'Annuleren')}
                </button>
              )}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <input
                type="text"
                data-testid="addressbook-input-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={tr('Vor- und Nachname *', 'Full Name *', 'Volledige naam *')}
                style={{
                  padding: '8px 10px', borderRadius: 7, border: `1px solid ${c.border}`,
                  background: c.card, color: c.text, fontSize: 13, width: '100%', boxSizing: 'border-box',
                }}
              />
              <input
                type="email"
                data-testid="addressbook-input-email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={tr('E-Mail (optional)', 'Email (optional)', 'E-mail (optioneel)')}
                style={{
                  padding: '8px 10px', borderRadius: 7, border: `1px solid ${c.border}`,
                  background: c.card, color: c.text, fontSize: 13, width: '100%', boxSizing: 'border-box',
                }}
              />
              <input
                type="text"
                data-testid="addressbook-input-dept"
                value={department}
                onChange={(e) => setDepartment(e.target.value)}
                placeholder={tr('Straße / Abt. / Raum', 'Street / Dept / Room', 'Straat / Afdeling / Kamer')}
                style={{
                  padding: '8px 10px', borderRadius: 7, border: `1px solid ${c.border}`,
                  background: c.card, color: c.text, fontSize: 13, width: '100%', boxSizing: 'border-box',
                }}
              />
              <input
                type="text"
                data-testid="addressbook-input-shelf"
                value={shelf}
                onChange={(e) => setShelf(e.target.value)}
                placeholder={tr('Stammfach (z.B. S1)', 'Default Shelf (opt.)', 'Stamvak (opt.)')}
                style={{
                  padding: '8px 10px', borderRadius: 7, border: `1px solid ${c.border}`,
                  background: c.card, color: c.text, fontSize: 13, width: '100%', boxSizing: 'border-box',
                }}
              />
              <div style={{ gridColumn: '1 / -1', display: 'flex', gap: 6 }}>
                <input
                  type="text"
                  data-testid="addressbook-input-note"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder={tr(
                    '📌 Hinweis für Mitarbeiter (z.B. Stammkunde – legt immer in Fach S1 / Hinterlegungswunsch)',
                    '📌 Staff note (e.g. Regular customer – always shelf S1 / special instructions)',
                    '📌 Notitie voor medewerker (bijv. Vaste klant – altijd in vak S1)'
                  )}
                  style={{
                    flex: 1, minWidth: 0, padding: '8px 10px', borderRadius: 7, border: `1px solid ${c.border}`,
                    background: c.card, color: c.text, fontSize: 13, boxSizing: 'border-box',
                  }}
                />
                <button
                  type="submit"
                  data-testid="addressbook-add-btn"
                  disabled={busy || !name.trim()}
                  style={{
                    padding: '8px 16px', borderRadius: 7, border: 'none',
                    background: name.trim() ? '#2e7d32' : c.border, color: '#fff',
                    fontWeight: 700, fontSize: 13, cursor: name.trim() ? 'pointer' : 'not-allowed',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {editingId ? tr('Speichern', 'Save', 'Opslaan') : `+ ${tr('Hinzufügen', 'Add', 'Toevoegen')}`}
                </button>
              </div>
            </div>
          </form>

          {/* Suche & Liste */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={tr('🔍 Liste durchsuchen (Name, Hinweis, Fach, E-Mail)…', '🔍 Search list (Name, Note, Shelf, Email)…', '🔍 Zoeken in lijst…')}
              style={{
                flex: 1, padding: '8px 10px', borderRadius: 8, border: `1px solid ${c.border}`,
                background: c.card, color: c.text, fontSize: 13,
              }}
            />
            {contacts.length > 0 && (
              <button
                type="button"
                onClick={handleClearAll}
                style={{
                  padding: '7px 10px', borderRadius: 8, border: `1px solid ${c.border}`,
                  background: 'transparent', color: '#e5484d', fontSize: 12, fontWeight: 600, cursor: 'pointer',
                }}
              >
                🗑️ {tr('Alle löschen', 'Clear all', 'Alles wissen')}
              </button>
            )}
          </div>

          {filtered.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '24px 12px', color: c.sub, fontSize: 13 }}>
              {contacts.length === 0
                ? tr('Noch keine Einträge auf der Liste. Lade oben eine CSV hoch oder füge jemanden hinzu.', 'No recipients in list yet.', 'Nog geen vermeldingen in de lijst.')
                : tr('Keine Treffer für diese Suche.', 'No matches found.', 'Geen resultaten gevonden.')}
            </div>
          ) : (
            <div style={{ maxHeight: 240, overflowY: 'auto', border: `1px solid ${c.border}`, borderRadius: 8 }}>
              {filtered.map((item, idx) => (
                <div
                  key={item.id}
                  style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '9px 12px',
                    borderBottom: idx < filtered.length - 1 ? `1px solid ${c.border}` : 'none',
                    background: editingId === item.id ? (dark ? '#172234' : '#f0f7ff') : 'transparent',
                    fontSize: 13,
                  }}
                >
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontWeight: 700, color: c.text, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 6 }}>
                      <span>{item.name}</span>
                      {item.shelf && (
                        <span style={{
                          padding: '1px 6px', borderRadius: 6, fontSize: 11,
                          background: dark ? '#1e3a5f' : '#e3f2fd', color: dark ? '#90caf9' : '#1565c0',
                        }}>
                          🗄️ {t('ui.addressBook.shelfBadge', { shelf: item.shelf })}
                        </span>
                      )}
                    </div>
                    {item.note && (
                      <div
                        data-testid="addressbook-item-note"
                        style={{
                          marginTop: 3, fontSize: 12, fontWeight: 600,
                          color: dark ? '#fcd34d' : '#b45309',
                        }}
                      >
                        📌 {item.note}
                      </div>
                    )}
                    <div style={{ fontSize: 12, color: c.sub, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginTop: 2 }}>
                      {[item.email && `📧 ${item.email}`, item.department && `🏢 ${item.department}`].filter(Boolean).join(' · ') || tr('Kein E-Mail/Abteilungs-Eintrag (nur Mitarbeiter-Hinweis)', 'Staff note / shelf only (no email)', 'Alleen medewerkers-notitie / stamvak')}
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <button
                      type="button"
                      data-testid={`addressbook-edit-${idx}`}
                      onClick={() => handleStartEdit(item)}
                      title={tr('Bearbeiten', 'Edit', 'Bewerken')}
                      style={{
                        background: 'transparent', border: 'none', color: c.text,
                        fontSize: 14, cursor: 'pointer', padding: '4px 6px',
                      }}
                    >
                      ✏️
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDelete(item.id)}
                      title={tr('Löschen', 'Delete', 'Verwijderen')}
                      style={{
                        background: 'transparent', border: 'none', color: '#e5484d',
                        fontSize: 16, cursor: 'pointer', padding: '4px 8px',
                      }}
                    >
                      ✕
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: '12px 18px', borderTop: `1px solid ${c.border}`, display: 'flex', justifyContent: 'flex-end' }}>
          <button
            type="button"
            onClick={onClose}
            style={{
              padding: '10px 22px', borderRadius: 9, border: 'none',
              background: c.primary, color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer',
            }}
          >
            {tr('Fertig', 'Done', 'Klaar')}
          </button>
        </div>
      </div>
    </div>
  );
}

